import { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { FileOutlined, UserOutlined } from "@ant-design/icons";

import CustomLayout from "../../../components/CustomLayout/CustomLayout";
import TittleLine from "../../../components/Common/TittleLine";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import CustomButton from "../../../components/CustomButton/CustomButton";
import Spinner from "../../../components/Common/Spinner";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomWindows from "../../../components/CustomWindows/CustomWindows";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import CustomModal from "../../../components/CustomModal/CustomModal";
import Text from "../../../components/Common/Text";
import ModalVisualizadorDocumentos from "../components/ModalVisualizadorDocumentos";
import ModalComentarios from "../components/ModalComentarios";

import { useMountEffect } from "../../../hooks/LifeCycle";

import { mostrarErrorGenerico } from "../../../utils/AlertUtils";
import { isObjectEmpty } from "../../../utils/ObjectUtil";
import {
  hasHojaQuirurgica,
  getHojaQuirurgica,
} from "../../../utils/HojaQuirurgicaStorage";
import { cargarCoberturasPaciente } from "../utils/CoberturaUtils";

import HojasQuirurgicasActions from "../../../stores/hospitalizacion/hojasQuirurgicas/Actions";
import useValidacionesHojaQXScreen from "../hooks/useValidacionesHojaQXScreen";
import {
  columnasValidacionesHojaQX,
  columnasAnexos,
  renderActionColumn,
  prepararDataAnexos,
} from "../utils/ValidacionesHojaQXScreenTableUtils";
import { MODAL_ANEXOS_MAX_WIDTH } from "../utils/ValidacionesHojaQXScreenConstants";

import { columnasProcedimientosDetalle } from "../utils/HospitalizacionConstant";
import useEmpresa from "../../../hooks/useEmpresa";

import {
  HorizontalCard,
  CardHeader,
  CardTitle,
  FieldsGrid,
  FieldContainer,
  FieldLabel,
  FieldValue,
  LoadingContainer,
  LoadingText,
  NotFoundContainer,
  NotFoundTitle,
  NotFoundText,
  EmailLink,
  ContainerWrapper,
  ContentContainer,
} from "../styles/DetallesPacienteStyles";

// Constantes
const WINDOW_IDS = {
  DETALLES_PACIENTE: "detalles-paciente",
  ESTATUS_COBERTURAS: "estatus-coberturas",
  ESTATUS_COBERTURAS_HOJA_QX: "estatus-coberturas-hoja-qx",
};

const WINDOW_TITLES = {
  DETALLES_PACIENTE: "Datos paciente ingresado",
  ESTATUS_COBERTURAS: "Estado de coberturas",
  ESTATUS_COBERTURAS_HOJA_QX: "Estatus de coberturas",
};

// Utilidades para formateo de datos
const DataFormatters = {
  formatDate: (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "N/A";
    }
  },

  formatAge: (age) => {
    return age ? `${age} años` : "N/A";
  },

  formatEmail: (email) => {
    return email ? `mailto:${email}` : "";
  },
};

// Componentes de renderizado
const RenderDetallesPaciente = ({
  paciente,
  hasExistingHoja,
  handleValidarHojaQX,
  isLoading,
  hojaQuirurgicaLoading,
}) => {
  const renderPersonalInfo = () => (
    <Section testid="detalles-paciente-section">
      <HorizontalCard
        style={{ marginTop: "15px" }}
        testid="detalles-paciente-card"
      >
        <CardHeader testid="detalles-paciente-header">
          <CardTitle testid="detalles-paciente-title">
            Información Personal
          </CardTitle>
        </CardHeader>
        <FieldsGrid testid="detalles-paciente-fields">
          <FieldContainer testid="field-numero-asegurado">
            <FieldLabel testid="label-numero-asegurado">
              Número de Asegurado
            </FieldLabel>
            <FieldValue testid="value-numero-asegurado">
              {paciente?.numeroPlastico || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-cedula">
            <FieldLabel testid="label-cedula">Cédula o Pasaporte</FieldLabel>
            <FieldValue testid="value-cedula">
              {paciente?.documento || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-nss">
            <FieldLabel testid="label-nss">NSS</FieldLabel>
            <FieldValue testid="value-nss">
              {paciente?.numeroSeguridadSocial || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-nombre">
            <FieldLabel testid="label-nombre">Nombre Completo</FieldLabel>
            <FieldValue testid="value-nombre">
              {paciente?.nombreDelPaciente || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-sexo">
            <FieldLabel testid="label-sexo">Sexo</FieldLabel>
            <FieldValue testid="value-sexo">
              {paciente?.sexo || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-edad">
            <FieldLabel testid="label-edad">Edad</FieldLabel>
            <FieldValue testid="value-edad">
              {DataFormatters.formatAge(paciente?.edad)}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-fecha-nacimiento">
            <FieldLabel testid="label-fecha-nacimiento">
              Fecha de Nacimiento
            </FieldLabel>
            <FieldValue testid="value-fecha-nacimiento">
              {DataFormatters.formatDate(paciente?.fechaNacimiento)}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-telefono">
            <FieldLabel testid="label-telefono">Teléfono</FieldLabel>
            <FieldValue testid="value-telefono">
              {paciente?.telefono || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-correo">
            <FieldLabel testid="label-correo">Correo Electrónico</FieldLabel>
            <FieldValue testid="value-correo">
              <EmailLink
                href={DataFormatters.formatEmail(paciente?.correo)}
                testid="email-link"
              >
                {paciente?.correo || "N/A"}
              </EmailLink>
            </FieldValue>
          </FieldContainer>
        </FieldsGrid>
      </HorizontalCard>
    </Section>
  );

  const renderMedicalInfo = () => (
    <Section>
      <HorizontalCard style={{ marginTop: "5px" }}>
        <CardHeader>
          <CardTitle>Información médica</CardTitle>
        </CardHeader>
        <FieldsGrid>
          <FieldContainer>
            <FieldLabel>Número de ingreso</FieldLabel>
            <FieldValue>{paciente?.numeroIngreso || "N/A"}</FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Número de afiliado</FieldLabel>
            <FieldValue>
              {paciente?.codigoPersonaHospitalizada || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Fecha de ingreso</FieldLabel>
            <FieldValue>
              {DataFormatters.formatDate(paciente?.fechaIngreso)}
            </FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Fecha de egreso</FieldLabel>
            <FieldValue>
              {DataFormatters.formatDate(paciente?.fechaSalida)}
            </FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Tipo de Paciente</FieldLabel>
            <FieldValue>
              {paciente?.tipoPersonaHospitalizada || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Médico Tratante</FieldLabel>
            <FieldValue>{paciente?.medicosTratantes || "N/A"}</FieldValue>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>Diagnóstico</FieldLabel>
            <FieldValue>{paciente?.causaInternamiento || "N/A"}</FieldValue>
          </FieldContainer>
        </FieldsGrid>
      </HorizontalCard>
    </Section>
  );

  const renderActionButton = () => (
    <>
      <CustomSeparator height={"20px"} />
      <Section margin="0 0 0 0" testid="button-section">
        <CustomButton
          label={
            hasExistingHoja ? "Ver hoja quirúrgica" : "Agregar hoja quirúrgica"
          }
          onClick={handleValidarHojaQX}
          isLoading={isLoading || hojaQuirurgicaLoading}
          height="46px"
          width="200px"
          testid="btn-hoja-quirurgica"
        />
      </Section>
    </>
  );

  return (
    <>
      {renderPersonalInfo()}
      {renderMedicalInfo()}
      {renderActionButton()}
    </>
  );
};

const RenderEstatusCoberturas = ({
  paciente,
  coberturasPaciente,
  tipoReclamante,
}) => {
  const renderCoberturasTable = () => {
    return (
      <>
        <CustomSeparator height={"20px"} testid="separator-coberturas" />
        <Section testid="table-section">
          <CheckRender allowed={coberturasPaciente.length > 0}>
            <CustomTable
              columns={columnasProcedimientosDetalle(tipoReclamante)}
              dataSource={coberturasPaciente}
              pageSize={10}
              containerWidth="100%"
              width="100%"
              testid="coberturas-table"
            />
          </CheckRender>
          <CheckRender allowed={coberturasPaciente.length === 0}>
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: "#666",
                fontSize: "16px",
              }}
            >
              No hay coberturas disponibles para este paciente
            </div>
          </CheckRender>
        </Section>
      </>
    );
  };

  const renderPatientInfo = () => (
    <Section testid="estatus-coberturas-section">
      <HorizontalCard
        style={{ marginTop: "15px" }}
        testid="estatus-coberturas-card"
      >
        <CardHeader testid="estatus-coberturas-header">
          <CardTitle testid="estatus-coberturas-title">
            Información del paciente ingresado
          </CardTitle>
        </CardHeader>
        <FieldsGrid testid="estatus-coberturas-fields">
          <FieldContainer testid="field-numero-asegurado">
            <FieldLabel testid="label-numero-asegurado">
              Número de Asegurado
            </FieldLabel>
            <FieldValue testid="value-numero-asegurado">
              {paciente?.numeroPlastico || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-numero-ingreso">
            <FieldLabel testid="label-numero-ingreso">
              Número de ingreso
            </FieldLabel>
            <FieldValue testid="value-numero-ingreso">
              {paciente?.numeroIngreso || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-paciente">
            <FieldLabel testid="label-paciente">Paciente</FieldLabel>
            <FieldValue testid="value-paciente">
              {paciente?.nombreDelPaciente || "N/A"}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-fecha-ingreso">
            <FieldLabel testid="label-fecha-ingreso">
              Fecha de ingreso
            </FieldLabel>
            <FieldValue testid="value-fecha-ingreso">
              {DataFormatters.formatDate(paciente?.fechaIngreso)}
            </FieldValue>
          </FieldContainer>
          <FieldContainer testid="field-fecha-egreso">
            <FieldLabel testid="label-fecha-egreso">Fecha de egreso</FieldLabel>
            <FieldValue testid="value-fecha-egreso">
              {DataFormatters.formatDate(paciente?.fechaSalida)}
            </FieldValue>
          </FieldContainer>
        </FieldsGrid>
      </HorizontalCard>
    </Section>
  );

  return (
    <>
      {renderPatientInfo()}
      {renderCoberturasTable()}
    </>
  );
};

const RenderEstatusCoberturasHojaQX = ({
  registros,
  numeroIngreso,
  processing,
  visibleActionsMenu,
  setVisibleActionsMenu,
  showModalAnexos,
  anexosAMostrar,
  modalDocumentoVisible,
  documentoModal,
  showModalComentarios,
  comentariosAMostrar,
  handleMenuClick,
  handleCloseModalAnexos,
  abrirModalDocumento,
  cerrarModalDocumento,
  handleCloseModalComentarios,
  generarRecordKey,
  actionsOptions,
  tipoReclamante,
}) => {
  const empresa = useEmpresa();

  const renderActionColumnCallback = useCallback(
    (record) => {
      return renderActionColumn({
        record,
        visibleActionsMenu,
        setVisibleActionsMenu,
        empresa,
        actionsOptions,
        handleMenuClick,
        generarRecordKey,
      });
    },
    [
      visibleActionsMenu,
      empresa,
      actionsOptions,
      handleMenuClick,
      generarRecordKey,
      setVisibleActionsMenu,
    ],
  );

  const columns = useMemo(
    () =>
      columnasValidacionesHojaQX({
        renderActionColumn: renderActionColumnCallback,
        tipoReclamante,
      }),
    [renderActionColumnCallback, tipoReclamante],
  );

  const columnsAnexosMemo = useMemo(
    () => columnasAnexos({ abrirModalDocumento }),
    [abrirModalDocumento],
  );

  const dataAnexos = useMemo(
    () => prepararDataAnexos(anexosAMostrar),
    [anexosAMostrar],
  );

  return (
    <>
      {/* <Section testid="filter-section">
        <BusquedaValidacionesHojaQXForm
          numeroIngreso={numeroIngreso}
          onStateChange={() => () => {}}
          onSearch={() => {}}
          onClean={() => {}}
          isLoading={processing}
          disabled={true}
          testid="busqueda-validaciones-form"
        />
      </Section> */}

      <Section
        testid="estatus-coberturas-hoja-qx-section"
        padding="0 0 100px 0"
      >
        <CheckRender allowed={registros && registros.length > 0}>
          <CustomTable
            columns={columns}
            dataSource={registros}
            pageSize={100}
            width="200%"
            testid="estatus-coberturas-hoja-qx-table"
          />
        </CheckRender>
        <CheckRender allowed={!registros || registros.length === 0}>
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: "16px",
            }}
            testid="no-estatus-coberturas-message"
          >
            No hay estatus de coberturas disponibles para este ingreso
          </div>
        </CheckRender>
      </Section>

      <CheckRender allowed={showModalAnexos}>
        <CustomModal
          isOpen={showModalAnexos}
          onClose={handleCloseModalAnexos}
          testid="modal-anexos"
          maxWidth={MODAL_ANEXOS_MAX_WIDTH}
        >
          <Section flexDirection="column">
            <Text
              size="20px"
              weight="bold"
              color={empresa?.COLOR_PRINCIPAL}
              margin="0 0 20px 0"
            >
              Anexos
            </Text>
            <CheckRender allowed={anexosAMostrar.length > 0}>
              <CustomTable
                columns={columnsAnexosMemo}
                dataSource={dataAnexos}
                pageSize={10}
                containerWidth="100%"
                width="100%"
                testid="anexos-table"
              />
            </CheckRender>
            <CheckRender allowed={anexosAMostrar.length === 0}>
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                No hay anexos disponibles para esta cobertura
              </div>
            </CheckRender>
          </Section>
        </CustomModal>
      </CheckRender>

      <ModalVisualizadorDocumentos
        visible={modalDocumentoVisible}
        documento={documentoModal}
        onClose={cerrarModalDocumento}
      />

      <ModalComentarios
        isOpen={showModalComentarios}
        onClose={handleCloseModalComentarios}
        comentariosAMostrar={comentariosAMostrar}
      />
    </>
  );
};

// Componente principal
const DetallesPacienteIngresado = (props) => {
  const { numeroIngreso } = useParams();
  const history = useHistory();

  // Estados
  const [alertConfig, setAlertConfig] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [hasExistingHoja, setHasExistingHoja] = useState(true);
  const [coberturasPaciente, setCoberturasPaciente] = useState([]);
  const [showModalValidacion, setShowModalValidacion] = useState(false);
  const [showModalYaTieneHojaQX, setShowModalYaTieneHojaQX] = useState(false);

  // Destructuring de props
  const {
    authUserData,
    consultarPacientesData,
    consultarPacientesLoading,
    consultarPacientesError,
    validarHojaQuirurgicaLoading,
    validarHojaQuirurgicaError,
    consultarEstatusHojasQXData,
    consultarEstatusHojasQXLoading,
    consultarTipoPssNoMedicoData,
    consultarPacientes,
    consultarEstatusHojasQX,
  } = props;

  // Valores calculados
  const pacientesIngresados = useMemo(
    () => consultarPacientesData?.data?.ingresos || [],
    [consultarPacientesData],
  );

  const paciente = useMemo(
    () => pacientesIngresados.find((p) => p.numeroIngreso === numeroIngreso),
    [pacientesIngresados, numeroIngreso],
  );

  // Funciones de utilidad
  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleError = useCallback((error) => {
    mostrarErrorGenerico(error, null, setAlertConfig, setShowAlert);
  }, []);

  const handleValidarHojaQX = useCallback(() => {
    // Verificar si ya tiene hoja QX
    if (paciente?.tieneHojaQx === "S") {
      setShowModalYaTieneHojaQX(true);
      return;
    }

    // Si no tiene hoja QX, continuar con el flujo normal
    setShowModalValidacion(true);
  }, [paciente?.tieneHojaQx]);

  const handleCloseModalValidacion = useCallback(() => {
    setShowModalValidacion(false);
    // Redirigir a la página de validación de hoja quirúrgica
    history.push(`/validar-hoja-QX/${numeroIngreso}`);
  }, [history, numeroIngreso]);

  const {
    registros: registrosEstatusCoberturas,
    processing: processingEstatusCoberturas,
    visibleActionsMenu,
    showModalAnexos,
    anexosAMostrar,
    modalDocumentoVisible,
    documentoModal,
    showModalComentarios,
    comentariosAMostrar,
    actionsOptions,
    handleMenuClick: handleMenuClickEstatusCoberturas,
    handleCloseModalAnexos,
    abrirModalDocumento: abrirModalDocumentoEstatusCoberturas,
    cerrarModalDocumento: cerrarModalDocumentoEstatusCoberturas,
    handleCloseModalComentarios,
    setVisibleActionsMenu,
    generarRecordKey,
  } = useValidacionesHojaQXScreen({
    authUserData,
    consultarEstatusHojasQX,
    consultarEstatusHojasQXData,
    consultarEstatusHojasQXLoading,
    consultarEstatusHojasQXError: props.consultarEstatusHojasQXError,
    numeroIngresoInicial: numeroIngreso,
    autoBuscar: true,
    itemsPerPageOverride: 100,
  });

  const isLoadingCompleto = useMemo(() => {
    return (
      consultarPacientesLoading ||
      processingEstatusCoberturas ||
      consultarEstatusHojasQXLoading
    );
  }, [
    consultarPacientesLoading,
    processingEstatusCoberturas,
    consultarEstatusHojasQXLoading,
  ]);

  const cargarCoberturasPacienteCallback = useCallback(() => {
    const nombrePrestador = authUserData?.prestador?.nombre || "";
    const coberturas = cargarCoberturasPaciente(
      numeroIngreso,
      nombrePrestador,
      getHojaQuirurgica,
    );
    setCoberturasPaciente(coberturas);

    const hojaQuirurgica = getHojaQuirurgica(numeroIngreso);
    if (hojaQuirurgica?.coberturasMedicos) {
      const coberturasConMedicos = hojaQuirurgica.coberturasMedicos.map(
        (item, index) => ({
          key: item.id || index,
          codigoCobertura: item.cobertura?.id || "",
          nombreCobertura: item.cobertura?.cobertura || "",
          codigoPrestador: item.medico?.codigo || "",
          nombrePrestador: item.medico?.nombre || "",
          status: "Pendiente",
          fechaCreacion: new Date().toLocaleDateString(),
        }),
      );
      setCoberturasPaciente(coberturasConMedicos);
    }
  }, [numeroIngreso, authUserData]);

  // Función para consultar paciente específico
  const consultarPacienteEspecifico = useCallback(() => {
    if (!numeroIngreso || !authUserData?.prestador?.codigo) return;

    const payload = {
      numeroMedico: authUserData.prestador.codigo,
      params: {
        numeroIngreso: numeroIngreso,
      },
    };

    consultarPacientes(payload);
  }, [numeroIngreso, authUserData, consultarPacientes]);

  // Función para obtener las ventanas
  const getWindows = useCallback(() => {
    const windows = [
      {
        id: WINDOW_IDS.DETALLES_PACIENTE,
        title: WINDOW_TITLES.DETALLES_PACIENTE,
        icon: <UserOutlined />,
        content: () => (
          <RenderDetallesPaciente
            paciente={paciente}
            hasExistingHoja={hasExistingHoja}
            handleValidarHojaQX={handleValidarHojaQX}
            isLoading={isLoadingCompleto}
            hojaQuirurgicaLoading={validarHojaQuirurgicaLoading || false}
          />
        ),
      },
    ];

    if (coberturasPaciente.length > 0) {
      windows.push({
        id: WINDOW_IDS.ESTATUS_COBERTURAS,
        title: WINDOW_TITLES.ESTATUS_COBERTURAS,
        icon: <FileOutlined />,
        content: () => (
          <RenderEstatusCoberturas
            paciente={paciente}
            coberturasPaciente={coberturasPaciente}
            tipoReclamante={consultarTipoPssNoMedicoData?.tipoReclamante}
          />
        ),
      });
    }

    if (registrosEstatusCoberturas && registrosEstatusCoberturas.length > 0) {
      windows.push({
        id: WINDOW_IDS.ESTATUS_COBERTURAS_HOJA_QX,
        title: WINDOW_TITLES.ESTATUS_COBERTURAS_HOJA_QX,
        icon: <FileOutlined />,
        content: () => (
          <RenderEstatusCoberturasHojaQX
            registros={registrosEstatusCoberturas}
            numeroIngreso={numeroIngreso}
            processing={processingEstatusCoberturas}
            visibleActionsMenu={visibleActionsMenu}
            setVisibleActionsMenu={setVisibleActionsMenu}
            showModalAnexos={showModalAnexos}
            anexosAMostrar={anexosAMostrar}
            modalDocumentoVisible={modalDocumentoVisible}
            documentoModal={documentoModal}
            showModalComentarios={showModalComentarios}
            comentariosAMostrar={comentariosAMostrar}
            handleMenuClick={handleMenuClickEstatusCoberturas}
            handleCloseModalAnexos={handleCloseModalAnexos}
            abrirModalDocumento={abrirModalDocumentoEstatusCoberturas}
            cerrarModalDocumento={cerrarModalDocumentoEstatusCoberturas}
            handleCloseModalComentarios={handleCloseModalComentarios}
            generarRecordKey={generarRecordKey}
            actionsOptions={actionsOptions}
            tipoReclamante={consultarTipoPssNoMedicoData?.tipoReclamante}
          />
        ),
      });
    }

    return windows;
  }, [
    paciente,
    hasExistingHoja,
    handleValidarHojaQX,
    isLoadingCompleto,
    showModalComentarios,
    comentariosAMostrar,
    handleCloseModalComentarios,
    validarHojaQuirurgicaLoading,
    coberturasPaciente,
    registrosEstatusCoberturas,
    numeroIngreso,
    processingEstatusCoberturas,
    visibleActionsMenu,
    showModalAnexos,
    anexosAMostrar,
    modalDocumentoVisible,
    documentoModal,
    handleMenuClickEstatusCoberturas,
    handleCloseModalAnexos,
    abrirModalDocumentoEstatusCoberturas,
    cerrarModalDocumentoEstatusCoberturas,
    setVisibleActionsMenu,
    generarRecordKey,
    actionsOptions,
    consultarTipoPssNoMedicoData?.tipoReclamante,
  ]);

  // Efectos
  useEffect(() => {
    if (validarHojaQuirurgicaError && !validarHojaQuirurgicaLoading) {
      setAlertConfig({
        title: "Error en la validación",
        message:
          validarHojaQuirurgicaError?.mensaje ||
          "Error al validar la hoja quirúrgica",
        type: "error",
      });
      setShowAlert(true);
    }
  }, [validarHojaQuirurgicaError, validarHojaQuirurgicaLoading]);

  useEffect(() => {
    if (!isObjectEmpty(consultarPacientesError)) {
      handleError(consultarPacientesError);
    }
  }, [consultarPacientesError, handleError]);

  useEffect(() => {
    if (numeroIngreso) {
      const existeHoja = hasHojaQuirurgica(numeroIngreso);
      setHasExistingHoja(existeHoja);
      if (existeHoja) {
        cargarCoberturasPacienteCallback();
      }
    }
  }, [numeroIngreso, cargarCoberturasPacienteCallback]);

  // Efecto para cargar datos del paciente al montar el componente
  useMountEffect(() => {
    consultarPacienteEspecifico();
    cargarCoberturasPacienteCallback();
  });

  // Renderizado de estados de carga
  if (isLoadingCompleto) {
    return (
      <CustomLayout
        showMenuNav={true}
        footerWidth={"auto"}
        testid="detalles-paciente-loading-layout"
      >
        <Section testid="loading-section">
          <LoadingContainer testid="loading-container">
            <Spinner size={60} testid="loading-spinner" />
            <LoadingText testid="loading-text">
              Cargando información del paciente...
            </LoadingText>
          </LoadingContainer>
        </Section>
      </CustomLayout>
    );
  }

  if (!paciente) {
    return (
      <CustomLayout
        showMenuNav={true}
        footerWidth={"auto"}
        testid="detalles-paciente-not-found-layout"
      >
        <Section testid="not-found-section">
          <NotFoundContainer testid="not-found-container">
            <NotFoundTitle testid="not-found-title">
              Paciente no encontrado
            </NotFoundTitle>
            <NotFoundText testid="not-found-text">
              No se encontró información para el número de ingreso:{" "}
              {numeroIngreso}
            </NotFoundText>
            <CustomButton
              width="100px"
              onClick={handleBack}
              label="Volver"
              testid="btn-volver"
            />
          </NotFoundContainer>
        </Section>
      </CustomLayout>
    );
  }

  // Renderizado principal
  return (
    <CustomLayout
      showMenuNav={true}
      footerWidth="auto"
      marginContainer="0 0 100px 0"
      testid="detalles-paciente-layout"
    >
      <Section testid="title-section">
        <TittleLine
          title={`Detalles del paciente ${paciente?.numeroIngreso || "N/A"}`}
          weight="bold"
          testid="detalles-paciente-title"
        />
      </Section>
      <CustomSeparator height={"20px"} testid="separator-title" />
      <ContainerWrapper testid="container-wrapper">
        <ContentContainer testid="content-container">
          <Section
            style={{
              height: "100%",
              minHeight: "600px",
              width: "100%",
            }}
            testid="windows-section"
          >
            <CustomWindows
              windows={getWindows()}
              defaultActiveWindow={0}
              tabPosition="top"
              onWindowChange={() => {}}
              testid="custom-windows"
            />
          </Section>

          <CheckRender allowed={showAlert}>
            <CustomAlert
              {...alertConfig}
              onConfirm={() => setShowAlert(false)}
              testid="custom-alert"
            />
          </CheckRender>

          {/* Modal informativo para validar hoja quirúrgica */}
          <CheckRender allowed={showModalValidacion}>
            <CustomAlert
              type="info"
              title="Información importante"
              message="Recuerda que puedes anexar un máximo de 10 soportes por solicitud. Si necesitas agregar más, comunícate con nuestros oficiales al 809-476-3535 para más información."
              onConfirm={handleCloseModalValidacion}
              confirmText="Entendido"
              maxWidth="500px"
            />
          </CheckRender>

          {/* Modal cuando ya tiene hoja QX */}
          <CheckRender allowed={showModalYaTieneHojaQX}>
            <CustomAlert
              type="warning"
              title="Hoja quirúrgica existente"
              message="Este ingreso ya tiene una hoja quirúrgica registrada."
              onConfirm={() => setShowModalYaTieneHojaQX(false)}
              confirmText="Entendido"
              maxWidth="500px"
            />
          </CheckRender>
        </ContentContainer>
      </ContainerWrapper>
    </CustomLayout>
  );
};

// Mapeo de estado y acciones
const mapStateToProps = (state) => ({
  authUserData: state.auth.authUserData,
  consultarPacientesData: state.hospitalizacion.consultarPacientesData,
  consultarPacientesLoading: state.hospitalizacion.consultarPacientesLoading,
  consultarPacientesError: state.hospitalizacion.consultarPacientesError,
  validarHojaQuirurgicaData: state.hospitalizacion.validarHojaQuirurgicaData,
  validarHojaQuirurgicaLoading:
    state.hospitalizacion.validarHojaQuirurgicaLoading,
  validarHojaQuirurgicaError: state.hospitalizacion.validarHojaQuirurgicaError,
  consultarEstatusHojasQXData:
    state.hospitalizacion.consultarEstatusHojasQXData,
  consultarEstatusHojasQXLoading:
    state.hospitalizacion.consultarEstatusHojasQXLoading,
  consultarEstatusHojasQXError:
    state.hospitalizacion.consultarEstatusHojasQXError,
  consultarDetalleAnexosData: state.hospitalizacion.consultarDetalleAnexosData,
  consultarDetalleAnexosLoading:
    state.hospitalizacion.consultarDetalleAnexosLoading,
  consultarDetalleAnexosError:
    state.hospitalizacion.consultarDetalleAnexosError,
  consultarTipoPssNoMedicoData: state.prestador.consultarTipoPssNoMedicoData,
});

const mapDispatchToProps = {
  consultarPacientes: HojasQuirurgicasActions.consultarPacientes,
  consultarEstatusHojasQX: HojasQuirurgicasActions.consultarEstatusHojasQX,
  consultarDetalleAnexos: HojasQuirurgicasActions.consultarDetalleAnexos,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(DetallesPacienteIngresado);
